import React from 'react';
import { Z_INDEX } from '@constants/layout';

const AUTO_HIDE_MS = 3000;
/** MUI's default resumeHideDuration is half the auto-hide window. */
const RESUME_MS = AUTO_HIDE_MS / 2;

interface VerificationSnackbarProps {
  message: string | null;
  onClose: () => void;
}

/**
 * Reproduces the MUI Snackbar this page used to render: it is the timer, the
 * pause-on-interaction and the dismiss-on-escape/click-away that made the
 * message clear itself. A static element would leave the message on screen
 * forever. Kept local rather than routed through the Redux NotificationManager,
 * which is a different surface (top-right, stacked, typed, closable).
 */
const VerificationSnackbar: React.FC<VerificationSnackbarProps> = ({ message, onClose }) => {
  const open = Boolean(message);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = React.useRef(onClose);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = React.useCallback(
    (durationMs: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => onCloseRef.current(), durationMs);
    },
    [clearTimer],
  );

  // Deliberately keyed on `open` alone: replacing the text of a visible message
  // did not restart the MUI timer either.
  React.useEffect(() => {
    if (!open) return undefined;
    startTimer(AUTO_HIDE_MS);
    return clearTimer;
  }, [open, startTimer, clearTimer]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.defaultPrevented && event.key === 'Escape') onCloseRef.current();
    };
    const resume = () => startTimer(RESUME_MS);

    let activated = false;
    const activation = setTimeout(() => {
      activated = true;
    }, 0);
    const handleClickAway = (event: MouseEvent) => {
      const node = rootRef.current;
      if (!activated || !node || node.contains(event.target as Node)) return;
      onCloseRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickAway);
    window.addEventListener('focus', resume);
    window.addEventListener('blur', clearTimer);
    return () => {
      clearTimeout(activation);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickAway);
      window.removeEventListener('focus', resume);
      window.removeEventListener('blur', clearTimer);
    };
  }, [open, startTimer, clearTimer]);

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      role="presentation"
      style={{ zIndex: Z_INDEX.NOTIFICATION }}
      className="fixed bottom-2 left-2 right-2 flex items-center min-[600px]:bottom-6 min-[600px]:left-6 min-[600px]:right-auto"
      onMouseEnter={clearTimer}
      onMouseLeave={() => startTimer(RESUME_MS)}
      onFocus={clearTimer}
      onBlur={() => startTimer(RESUME_MS)}
    >
      <div
        role="alert"
        className="revy-cjk min-w-[288px] max-w-[568px] rounded-sm bg-foreground px-4 py-1.5 text-sm leading-[1.5] text-background shadow-lg animate-in fade-in-0 zoom-in-95"
      >
        {message}
      </div>
    </div>
  );
};

export default VerificationSnackbar;
